import { isEmpty } from 'radash';
import { useState } from 'react';

import { Button } from '@/components/core/Button';
import { Callout } from '@/components/core/Callout';
import { Checkbox } from '@/components/core/Checkbox';
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogHeaderDefault,
} from '@/components/core/Dialog';
import { Input } from '@/components/core/Input';
import { DataModelAttributesSelector } from '@/components/shared';
import { useDataModel } from '@/hooks/useDataModel';
import { isItemNamespaceReadonly, useOrganization } from '@/hooks/useOrganization';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';
import { DataModelAttribute } from '@/types/DataModel';

import { useCreateBulkSubscription } from '../bulk-subscriptions.queries';
import { BulkSubscription } from '../bulk-subscriptions.types';

const LAYOUT_NAME = 'bulk_subscription';

interface NewBulkSubscriptionDialogProps {
    open: boolean;
    entity?: BulkSubscription;
    onClose: (createdBulkSubscriptionEntity?: BulkSubscription) => void;
}

const NewBulkSubscriptionDialog = ({
    open,
    onClose,
}: NewBulkSubscriptionDialogProps) => {

    const { t } = useTranslation();
    const { dataModel } = useDataModel();
    const { toast } = useToast();
    const { organization } = useOrganization();

    // Active bulk subscriptions mutate items, so recipients of shared items may only create inactive ones
    const canCreateActive = !isItemNamespaceReadonly(organization);

    const [selectedAttributes, setSelectedAttributes] = useState<string[]>([]);
    const [subscriptionName, setSubscriptionName] = useState('');
    const [subscriptionLabel, setSubscriptionLabel] = useState('');
    const [subscriptionDescription, setSubscriptionDescription] = useState('');
    const [subscriptionActive, setSubscriptionActive] = useState(canCreateActive);

    const selectableAttributes =
        dataModel?.filteredLayoutAttributes(LAYOUT_NAME)
            .filter(attribute => !dataModel?.isMetaAttribute(attribute.name)) || [];

    const { mutate: createBulkSubscription, isPending } = useCreateBulkSubscription({
        onSuccess: (newEntry) => {
            toast.success('BULK_SUBSCRIPTION.SAVED_SUCCESSFULLY');
            onClose(newEntry);
        },
        onError: (error) => {
            if (error.response.data.message) {
                toast.error(error.response.data.message);
            } else {
                toast.error('ERROR_OCCURRED');
            }
        }
    });

    const onCreate = () => {
        if (selectedAttributes.length === 0 || isEmpty(subscriptionName)) {
            toast.error('BULK_SUBSCRIPTION.MISSING_REQUIRED_FIELDS');
            return;
        } else {
            createBulkSubscription({
                name: subscriptionName,
                description: subscriptionDescription,
                label: isEmpty(subscriptionLabel) ? subscriptionName : subscriptionLabel,
                attributes: selectedAttributes,
                active: canCreateActive && subscriptionActive
            });
        }
    };

    return (
        <Dialog open={open} onOpenChange={() => onClose()}>
            <DialogContent
                variant="large"
                onPointerDownOutside={(e) => e.preventDefault()}
                onInteractOutside={(e) => e.preventDefault()}
            >
                <DialogHeader>
                    <DialogHeaderDefault>
                        {t('BULK_SUBSCRIPTION.NEW_SUBSCRIPTION')}
                    </DialogHeaderDefault>
                </DialogHeader>
                <DialogBody padding="none" className="tw-flex tw-flex-col tw-min-h-0 tw-h-[80vh] tw-px-6 tw-w-full tw-overflow-x-auto">
                    <Callout
                        canBeClosed={true}
                        variant="info"
                        className="tw-my-2"
                    >
                        {t('BULK_SUBSCRIPTION.NEW_SUBSCRIPTION_HINT')}
                    </Callout>
                    <div className="tw-flex tw-justify-center tw-items-center tw-py-3">
                        <Input
                            className="tw-w-3/4"
                            placeholder={t('BULK_SUBSCRIPTION.NAME')}
                            name="name"
                            value={subscriptionName}
                            onChange={(e) =>
                                setSubscriptionName(e.target.value)
                            }
                        />
                        <div className="tw-w-1/4 tw-flex tw-justify-center tw-items-center tw-px-6">
                            <label className="tw-ml-4 label-min-width tw-text-t-sm tw-max-w-[250px]">
                                {t('ACTIVE')}
                            </label>
                            <Checkbox
                                name="active"
                                isChecked={subscriptionActive}
                                disabled={!canCreateActive}
                                onCheckedChange={(value) => {
                                    if (typeof value === 'boolean') {
                                        setSubscriptionActive(value);
                                    }
                                }}
                            />
                        </div>
                    </div>
                    <div className="tw-flex tw-justify-center tw-items-center tw-py-3">
                        <Input
                            className="tw-flex-1"
                            placeholder={t('CUSTOMIZATION.LABEL')}
                            name="label"
                            value={subscriptionLabel}
                            onChange={(e) =>
                                setSubscriptionLabel(e.target.value)
                            }
                        />
                    </div>
                    <div className="tw-flex tw-justify-center tw-items-center tw-py-3">
                        <Input
                            className="tw-flex-1"
                            placeholder={t('DATA_MODEL.DESCRIPTION')}
                            name="description"
                            value={subscriptionDescription}
                            onChange={(e) =>
                                setSubscriptionDescription(e.target.value)
                            }
                        />
                    </div>
                    {dataModel && (
                        <div className="tw-mt-3 tw-h-full">
                            <DataModelAttributesSelector
                                attributes={selectableAttributes}
                                onSelectedChange={(
                                    selected: DataModelAttribute[],
                                ) =>
                                    setSelectedAttributes(
                                        selected.map(
                                            (attribute) => attribute.name,
                                        ),
                                    )
                                }
                            />
                        </div>
                    )}
                </DialogBody>
                <DialogFooter>
                    <Button
                        type="submit"
                        className="tw-mr-1"
                        disabled={isPending}
                        loading={isPending}
                        onClick={onCreate}
                    >
                        {t('SAVE')}
                    </Button>
                    <Button
                        className="tw-mr-1"
                        onClick={() => onClose()}
                        disabled={isPending}
                        variant="secondary"
                    >
                        {t('CLOSE')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export { NewBulkSubscriptionDialog };
